// (P75, P86) The hall's board of the last dances. When nothing's dancing, a letter board in a
// brass frame hangs on two chains in front of the backdrop, between the curtains, and lists
// the dances held here: when (the date and the time, in the visitor's own clock), what was
// danced (its title links to its page), who danced it, how long it runs, and a brass "Watch it"
// ticket that replays it on this floor. Eight to a page, paged with two brass knobs.
//
// It is part of the stage, not a panel over it: the board is a rectangle in the world (`rect`),
// and each frame its four corners are projected through the camera and the board's page is
// mapped onto them with a CSS matrix3d, so it sits on the stage in perspective from above,
// walking and through your own eyes, and moves with the camera. HTML rather than a texture:
// the words stay sharp (the tilt-shift blur at the top of the view would soften a texture) and
// the tickets are real buttons.
//
// How it's printed follows how big it shows: "wide" (one line a dance, eight to a page) when it
// is big enough to read that way, "compact" (two lines a dance, three to a page) when it is
// further off. Where it can't be fitted (most of it off the screen, as on a phone, whose view
// shows the stage's right side cut off), it is the same board held flat over the stage, four to a
// page. When the stage is out of view the page's plaque ("See the last dance") stands in for it.
// While a dance or a replay is on, it isn't shown.
import * as THREE from 'three';
import { someNames, danceWhen, danceLength, type Past } from './replay.js';
import { danceTitle } from './score.js';

const CSS = `
.pz-board{position:absolute;left:0;top:0;z-index:2;pointer-events:auto;transform-origin:0 0;box-sizing:border-box;display:flex;flex-direction:column;
  font-family:ui-rounded,'SF Pro Rounded','Nunito',system-ui,sans-serif;color:#fff3c8;padding:12px;border-radius:6px;
  background:linear-gradient(180deg,#f0d88f 0%,#d6b05a 30%,#b8913c 70%,#9c7a2c 100%);
  box-shadow:0 0 0 2px #3a2a20,inset 0 1px 0 rgba(255,248,220,.7),0 30px 44px -22px rgba(10,4,14,.85)}
.pz-board[hidden]{display:none}
.pz-board .bulbs:before,.pz-board .bulbs:after{content:"";position:absolute;left:14px;right:14px;height:8px;
  background:radial-gradient(circle,#fffbe8 0 1.8px,#ffd36b 2.6px,rgba(255,211,107,0) 4px) 0 0/18px 8px repeat-x}
.pz-board .bulbs:before{top:2px}.pz-board .bulbs:after{bottom:2px}
.pz-board .chains{position:absolute;left:0;right:0;bottom:100%;height:var(--chain,64px);pointer-events:none}
.pz-board .chains:before,.pz-board .chains:after{content:"";position:absolute;bottom:0;top:0;width:7px;border-radius:3px;
  background:repeating-linear-gradient(180deg,#e3c473 0 7px,#8a6a24 7px 9px,#c9a24a 9px 13px,#6e5320 13px 14px)}
.pz-board .chains:before{left:16%}.pz-board .chains:after{right:16%}
.pz-board.flat .chains{display:none}
.pz-board .face{position:relative;flex:1;min-height:0;display:flex;flex-direction:column;border-radius:3px;
  background:radial-gradient(130% 120% at 50% -10%,#45304f 0%,#2c1f34 48%,#1d1523 100%);
  box-shadow:0 0 0 2px #3a2a20,inset 0 0 0 3px rgba(233,207,131,.22),inset 0 10px 24px -10px rgba(0,0,0,.6)}
.pz-board header{display:flex;align-items:center;gap:14px;padding:8px 14px 7px 18px;border-bottom:1.5px solid rgba(233,207,131,.38)}
.pz-board header .h{flex:1;min-width:0;display:flex;align-items:baseline;gap:12px;flex-wrap:wrap}
.pz-board header b{font-size:28px;font-weight:800;line-height:1.1;color:#fff3c8;text-shadow:0 0 14px rgba(255,214,140,.35)}
.pz-board header small{font-size:19px;font-weight:800;color:#ff8fb8}
.pz-board .pager{display:flex;align-items:center;gap:8px;font-size:17px;font-weight:700;color:#d9c49c;font-variant-numeric:tabular-nums;white-space:nowrap}
.pz-board .pager[hidden]{display:none}
.pz-board .pager button{width:34px;height:34px;border-radius:50%;border:2px solid #3a2a20;cursor:pointer;padding:0;display:grid;place-items:center;
  font:800 19px/1 ui-rounded,'SF Pro Rounded','Nunito',system-ui,sans-serif;color:#4a3712;background:radial-gradient(circle at 35% 30%,#f6e2a2,#d6b05a 55%,#a8842f);
  box-shadow:0 3px 6px -3px rgba(0,0,0,.6)}
.pz-board .pager button:disabled{opacity:.35;cursor:default}
.pz-board .pager button:not(:disabled):hover{background:radial-gradient(circle at 35% 30%,#fff0bf,#e3c473 55%,#b8913c)}
/* the dances: each an entry, its title, then when · who danced it · how long, its ticket beside them */
.pz-board ol{list-style:none;margin:0;padding:0;flex:1;min-height:0;display:flex;flex-direction:column;position:relative}
.pz-board li{display:grid;grid-template-columns:minmax(0,1fr) auto;grid-template-rows:auto auto;column-gap:14px;row-gap:1px;align-content:center;
  height:var(--row,auto);padding:0 12px 0 18px;border-top:1px dashed rgba(233,207,131,.2);box-sizing:border-box}
.pz-board li.first{border-top:0}
.pz-board li.band{background:rgba(255,228,170,.05)}
.pz-board li.latest{background:linear-gradient(90deg,rgba(255,214,140,.15),rgba(255,214,140,.04))}
.pz-board li[hidden]{display:none!important}
.pz-board li:hover{background:rgba(255,228,170,.11)}
.pz-board li.chosen{background:linear-gradient(90deg,rgba(255,214,140,.34),rgba(255,214,140,.16))!important}
.pz-board li.chosen .go{transform:translateY(1px);background:linear-gradient(180deg,#fff0bf,#e3c473 55%,#c49a43);box-shadow:inset 0 0 0 1.5px rgba(58,42,32,.85),0 0 16px rgba(255,214,120,.7)}
.pz-board .t{grid-row:1;grid-column:1;min-width:0;font-weight:800;color:#fff3c8;line-height:1.15;white-space:nowrap;overflow:hidden;text-decoration:none}
.pz-board .t.long{font-size:.8em!important;white-space:normal;line-height:1.05}
.pz-board a.t{text-decoration:underline dotted rgba(233,207,131,.55);text-underline-offset:.2em;text-decoration-thickness:.08em}
.pz-board a.t:hover{color:#ffe39a}
/* the line under the title, wrapping where it must; each part's dot sits in a margin the line's start
   cuts off, so a wrapped line never begins with one */
.pz-board .meta{grid-row:2;grid-column:1;display:block;overflow:hidden;min-width:0;line-height:1.28}
.pz-board .meta .in{display:flex;flex-wrap:wrap;align-items:baseline;margin-left:calc(-1 * var(--sep))}
.pz-board .date:before,.pz-board .by:before,.pz-board .len:before{content:"·";display:inline-block;width:var(--sep);text-align:center;color:#a8906c;font-weight:600}
.pz-board .date{font-weight:600;color:#d9c49c;white-space:nowrap}
.pz-board .time{font-weight:800;color:#fff3c8;font-variant-numeric:tabular-nums;white-space:nowrap;margin-left:.35em}
.pz-board .by{font-weight:600;color:#f6c9d8;white-space:nowrap}
.pz-board .len{font-weight:600;color:#d9c49c;white-space:nowrap;font-variant-numeric:tabular-nums}
.pz-board .dots{display:inline-flex;margin-right:.35em;vertical-align:-.05em}
.pz-board .dots i{width:.62em;height:.62em;border-radius:50%;margin-right:-.2em;box-shadow:0 0 0 .09em #2c1f34,inset 0 -.12em 0 rgba(0,0,0,.18)}
/* three lines: when and how long, then who danced it on a line of its own, every entry alike */
.pz-board.stack .len{order:1}.pz-board.stack .by{order:2;flex-basis:100%;padding-left:var(--sep)}.pz-board.stack .by:before{display:none}
.pz-board .go{grid-row:1 / -1;grid-column:2;align-self:center;cursor:pointer;white-space:nowrap;border:0;
  font:800 1em/1 ui-rounded,'SF Pro Rounded','Nunito',system-ui,sans-serif;color:#4a3712;padding:.4em .8em .45em .75em;border-radius:3px;
  background:linear-gradient(180deg,#f0d88f,#d6b05a 55%,#b8913c);box-shadow:inset 0 0 0 1.5px rgba(58,42,32,.85),0 4px 8px -4px rgba(0,0,0,.7);
  -webkit-mask:radial-gradient(circle at 0 50%,#0000 .3em,#000 calc(.3em + .5px)) left/51% 100% no-repeat,radial-gradient(circle at 100% 50%,#0000 .3em,#000 calc(.3em + .5px)) right/51% 100% no-repeat;
          mask:radial-gradient(circle at 0 50%,#0000 .3em,#000 calc(.3em + .5px)) left/51% 100% no-repeat,radial-gradient(circle at 100% 50%,#0000 .3em,#000 calc(.3em + .5px)) right/51% 100% no-repeat}
.pz-board .go:hover{background:linear-gradient(180deg,#fff0bf,#e3c473 55%,#c49a43)}
.pz-board .go:active{transform:translateY(1px)}
.pz-board button:focus-visible,.pz-board a:focus-visible{outline:2px solid #ffd36b;outline-offset:2px}

/* wide: one line a dance, eight to a page, at the board's own scale (960 px across, mapped onto the
   stage). The ticket leads each line like a stub, beside the date, the time and the title, so what
   belongs together sits close even when the board is seen at a slant from above; a band on every
   other line and a dotted leader carry the eye on to who danced it and how long it runs */
.pz-board.wide ol{display:grid;align-content:start;grid-template-columns:max-content max-content max-content minmax(0,max-content) minmax(0,1fr) max-content}
.pz-board.wide li{grid-column:1 / -1;grid-template-columns:subgrid;grid-template-rows:auto;column-gap:16px;align-items:center;padding:0 20px 0 12px}
.pz-board.wide .meta,.pz-board.wide .meta .in{display:contents}
.pz-board.wide .date:before,.pz-board.wide .by:before,.pz-board.wide .len:before{display:none}
.pz-board.wide .go{grid-column:1;grid-row:1;font-size:19px}
.pz-board.wide .date{grid-column:2;grid-row:1;font-size:21px}
.pz-board.wide .time{grid-column:3;grid-row:1;font-size:23px;margin-left:0}
.pz-board.wide .t{grid-column:4;grid-row:1;font-size:24px}
.pz-board.wide .by{grid-column:5;grid-row:1;font-size:21px;display:flex;align-items:center;min-width:0}
.pz-board.wide .by:after{content:"";flex:1;min-width:10px;margin:0 0 0 12px;height:0;border-bottom:3px dotted rgba(233,207,131,.32)}
.pz-board.wide .len{grid-column:6;grid-row:1;font-size:20px}
.pz-board.wide li.latest{background:linear-gradient(90deg,rgba(255,214,140,.17),rgba(255,214,140,.04) 70%,rgba(255,214,140,0))}
/* compact: further off, one column of three, two lines each (540 px across) */
.pz-board.compact,.pz-board.flat{padding:10px}
.pz-board.compact .bulbs:before,.pz-board.compact .bulbs:after,.pz-board.flat .bulbs:before,.pz-board.flat .bulbs:after{height:6px;left:10px;right:10px;background-size:14px 6px}
.pz-board.compact{--sep:20px}
.pz-board.compact header small,.pz-board.flat header small{display:none}
.pz-board.compact header{padding:6px 10px 5px 14px}
.pz-board.compact header b{font-size:22px}
.pz-board.compact .pager{font-size:15px;gap:6px}.pz-board.compact .pager button{width:30px;height:30px;font-size:17px}
.pz-board.compact li{padding:0 10px 0 14px}
.pz-board.compact .t{font-size:20px}
.pz-board.compact .meta{font-size:16px}
.pz-board.compact .time{font-size:17px}
.pz-board.compact .go{font-size:16px}
/* flat: held flat over the stage, in screen pixels */
.pz-board.flat{padding:8px;border-radius:5px;--sep:15px}
.pz-board.flat header{padding:6px 8px 5px 12px;gap:8px}
.pz-board.flat header b{font-size:17px}
.pz-board.flat .pager{font-size:12.5px;gap:5px}.pz-board.flat .pager button{width:28px;height:28px;font-size:15px}
.pz-board.flat li{padding:7px 8px 7px 12px}
.pz-board.flat .t{font-size:15px}
.pz-board.flat .meta{font-size:12.5px}
.pz-board.flat .time{font-size:13px}
.pz-board.flat .go{font-size:13px}
html.pz-drawer-open .pz-board,html.pz-coaching .pz-board{visibility:hidden}
`;

// where the board hangs: a rectangle facing the hall (the +z side), in world units
export type BoardRect = { x0: number; x1: number; y0: number; y1: number; z: number; chainTo?: number };
export type HallBoard = { update(cam: THREE.Camera, vw: number, vh: number): void; set(on: boolean, past: Past[], now: number): void; inView(): boolean };
type Mode = 'wide' | 'compact' | 'flat' | 'off';

// how each way of printing it lays out: its page width (CSS px) and the dances on a page
const WIDE_W = 960, COMPACT_W = 540, PER: Record<Mode, number> = { wide: 8, compact: 3, flat: 4, off: 8 };

// the CSS matrix3d that maps a w × h box onto a quad (top left, top right, bottom right, bottom left)
function quadMatrix(w: number, h: number, q: number[][]) {
  const [[x0, y0], [x1, y1], [x2, y2], [x3, y3]] = q;
  const dx1 = x1 - x2, dx2 = x3 - x2, dx3 = x0 - x1 + x2 - x3, dy1 = y1 - y2, dy2 = y3 - y2, dy3 = y0 - y1 + y2 - y3;
  const den = dx1 * dy2 - dx2 * dy1 || 1e-9;
  let g = (dx3 * dy2 - dx2 * dy3) / den, k = (dx1 * dy3 - dx3 * dy1) / den;
  let a = x1 - x0 + g * x1, b = x3 - x0 + k * x3, d = y1 - y0 + g * y1, e = y3 - y0 + k * y3;
  a /= w; d /= w; g /= w; b /= h; e /= h; k /= h;
  const f = (n: number) => +n.toFixed(7);
  return `matrix3d(${f(a)},${f(d)},0,${f(g)},${f(b)},${f(e)},0,${f(k)},0,0,1,0,${f(x0)},${f(y0)},0,1)`;
}

export function hallBoard(o: { ui: HTMLElement; rect: BoardRect; play: (i: number) => void }): HallBoard {
  const css = document.createElement('style'); css.textContent = CSS; document.head.appendChild(css);
  const R = o.rect, aspect = (R.y1 - R.y0) / (R.x1 - R.x0);
  const el = document.createElement('section'); el.className = 'pz-board wide'; el.hidden = true;
  el.innerHTML = '<span class="chains" aria-hidden="true"></span><span class="bulbs" aria-hidden="true"></span><div class="face">'
    + '<header><div class="h"><b>Earlier on this stage</b><small></small></div>'
    + '<div class="pager"><button type="button" class="prev" aria-label="Newer dances">‹</button><span></span><button type="button" class="next" aria-label="Older dances">›</button></div></header><ol></ol></div>';
  const list = el.querySelector('ol')!, kick = el.querySelector('header small')!;
  const pager = el.querySelector('.pager') as HTMLElement, pageText = pager.querySelector('span')!;
  const prev = pager.querySelector('.prev') as HTMLButtonElement, next = pager.querySelector('.next') as HTMLButtonElement;
  o.ui.appendChild(el);
  // a click on the board is the board's, not the camera's or a walk's
  for (const ev of ['pointerdown', 'touchstart'] as const) el.addEventListener(ev, (e) => e.stopPropagation(), { passive: true });
  // keys walk you: a ticket or a knob mustn't keep them
  el.addEventListener('keydown', (e) => { if (!['Enter', ' ', 'Tab'].includes(e.key)) (document.activeElement as HTMLElement | null)?.blur(); });
  let on = false, key = '', mode: Mode = 'off', page = 0, rows: HTMLLIElement[] = [], flatPer = 4, flatH = -1, fitTitles = true;
  const per = () => (mode === 'flat' ? flatPer : PER[mode]);
  const showPage = () => {
    const n = rows.length, size = per(), pages = Math.max(1, Math.ceil(n / size));
    page = Math.max(0, Math.min(page, pages - 1));
    // within the page: the first has no rule above it; every other row a faint band
    rows.forEach((li, i) => {
      const on = Math.floor(i / size) === page, k = i - page * size;
      li.hidden = !on;
      li.classList.toggle('first', on && k === 0);
      li.classList.toggle('band', on && k % 2 === 1 && i !== 0);
    });
    fitTitles = true;
    pager.hidden = n <= size;
    pageText.textContent = `${page * size + 1}–${Math.min(n, (page + 1) * size)} of ${n}`;
    prev.disabled = page === 0; next.disabled = page >= pages - 1;
    flatH = -1;
  };
  const turn = (by: number) => { page += by; showPage(); };
  // a ticket pressed: its row lights up, the board fades, and the replay begins (timed on the
  // frame clock, in update, so it is the same in a film as in a browser)
  let chosen: { li: HTMLLIElement; i: number; at: number } | null = null;
  const FADE_MS = 420;
  const choose = (li: HTMLLIElement, i: number) => { if (chosen) return; li.classList.add('chosen'); chosen = { li, i, at: performance.now() }; };
  prev.addEventListener('click', () => { turn(-1); prev.blur(); });
  next.addEventListener('click', () => { turn(1); next.blur(); });
  // the wheel over the board turns its pages (gently: one a notch, not a page a pixel)
  let wheelAt = 0;
  el.addEventListener('wheel', (e) => {
    e.stopPropagation(); e.preventDefault();
    const t = performance.now();
    if (t - wheelAt < 350 || Math.abs(e.deltaY) < 4) return;
    wheelAt = t; turn(e.deltaY > 0 ? 1 : -1);
  }, { passive: false });

  const setMode = (m: Mode) => {
    if (m === mode) return;
    // keep the dance at the top of the page in view across the change
    const first = page * per();
    mode = m;
    el.classList.toggle('wide', m === 'wide'); el.classList.toggle('compact', m === 'compact'); el.classList.toggle('flat', m === 'flat');
    el.classList.toggle('stack', m === 'flat' && el.classList.contains('narrow'));
    if (m === 'wide' || m === 'compact') {
      const w = m === 'wide' ? WIDE_W : COMPACT_W, h = Math.round(w * aspect);
      el.style.width = `${w}px`; el.style.height = `${h}px`;
      // the chains up to the fly bar, at the board's own scale
      el.style.setProperty('--chain', `${Math.round(((R.chainTo ?? R.y1) - R.y1) / (R.y1 - R.y0) * h)}px`);
      // the rows share what's left under the header
      const frame = m === 'wide' ? 24 : 20, head = m === 'wide' ? 52 : 44;
      el.style.setProperty('--row', `${Math.floor((h - frame - head - 4) / PER[m])}px`);
    } else { el.style.height = ''; el.style.removeProperty('--row'); }
    if (m !== 'off') { page = Math.floor(first / per()); showPage(); }
  };

  // the page's chrome, measured at most twice a second (not every frame): the top bar and its
  // label, the gutter, the dock, whether you're walking
  let kAt = -1e9;
  let kc = { clear: 64, g: 16, walking: false, dock: null as DOMRect | null };
  const chrome = () => {
    const now = performance.now();
    if (now - kAt < 500) return kc;
    kAt = now;
    const cs = getComputedStyle(document.documentElement), root = document.documentElement.classList;
    const label = document.querySelector('.pz-label')?.getBoundingClientRect();
    const g = parseFloat(cs.getPropertyValue('--pz-g')) || 16;
    kc = {
      clear: Math.max(parseFloat(cs.getPropertyValue('--pz-top-h')) || 64, label && label.height ? label.bottom : 0), g,
      walking: root.contains('pz-walking'), dock: document.querySelector('[data-pz="dock"]')?.getBoundingClientRect() || null,
    };
    return kc;
  };
  const P = new THREE.Vector3();
  const corners = [[R.x0, R.y1], [R.x1, R.y1], [R.x1, R.y0], [R.x0, R.y0]];
  let lastT = '';
  return {
    set(want, past) {
      on = want && past.length > 0;
      if (!on) { el.hidden = true; setMode('off'); return; }
      const top = past.slice(0, 20);
      const k = JSON.stringify(top.map((p) => [p.at, p.line, p.dance, p.cast?.length]));
      if (k === key) return;
      key = k;
      kick.textContent = top.length === 1 ? 'the last dance here' : `the last ${top.length} dances here`;
      el.setAttribute('aria-label', `Earlier on this stage: the last ${top.length === 1 ? 'dance' : `${top.length} dances`} here`);
      list.innerHTML = '';
      rows = top.map((p, i) => {
        const li = document.createElement('li');
        const crew = p['crew-name'];
        const cast = (p.cast || []).filter((c) => p.dancers.includes(c.slug));
        const names = crew || someNames(cast.map((c) => c.name), 2) || p.line.split(' — ')[0];
        const title = cap(danceTitle(p) || p.title || 'A dance');
        // the title is the way to the dance's own page
        const t = document.createElement(p.dance && p.dance !== 'dance' ? 'a' : 'span'); t.className = 't'; t.textContent = title;
        if (t instanceof HTMLAnchorElement) { t.href = `/dance/${encodeURIComponent(p.dance)}`; t.title = `${title}: its page`; }
        const w = danceWhen(p.at);
        const meta = document.createElement('span'); meta.className = 'meta';
        const when = document.createElement('time'); when.dateTime = w.iso; when.style.display = 'contents';
        const date = document.createElement('span'); date.className = 'date'; date.textContent = w.date;
        const time = document.createElement('span'); time.className = 'time'; time.textContent = w.time;
        when.append(date, time);
        const by = document.createElement('span'); by.className = 'by';
        if (cast.length) { const d = document.createElement('span'); d.className = 'dots'; d.setAttribute('aria-hidden', 'true'); for (const c of cast.slice(0, 4)) { const x = document.createElement('i'); x.style.background = c.color; d.append(x); } by.append(d); }
        by.append(names);
        const len = document.createElement('span'); len.className = 'len'; len.textContent = danceLength(p);
        const inner = document.createElement('span'); inner.className = 'in';
        inner.append(when, by, len); meta.append(inner);
        const b = document.createElement('button'); b.type = 'button'; b.className = 'go'; b.textContent = '▶ Watch it';
        b.setAttribute('aria-label', `Watch ${title}, danced by ${names} on ${w.date} at ${w.time}, again on the floor`);
        b.addEventListener('click', () => { b.blur(); choose(li, i); });
        if (i === 0) li.className = 'latest';
        li.append(t, meta, b);
        // (in wide, the grid puts the ticket first; the order here is the reading order elsewhere)
        return li;
      });
      list.append(...rows);
      page = 0;
      if (mode !== 'off') showPage();
    },
    // where the board shows: fitted to the stage when it's big enough on the screen to read, held
    // flat over the stage when it isn't but the stage is in view, and otherwise not at all
    update(cam, vw, vh) {
      if (chosen) {
        const k = (performance.now() - chosen.at) / FADE_MS;
        el.style.opacity = String(Math.max(0, Math.min(1, 1.6 - 1.6 * k)));
        if (k >= 1) { const c = chosen; chosen = null; c.li.classList.remove('chosen'); o.play(c.i); el.style.opacity = ''; return; }
      }
      if (!on) return;
      const q: number[][] = [];
      let behind = false;
      for (const [x, y] of corners) {
        P.set(x, y, R.z).project(cam);
        if (P.z >= 1 || P.z <= -1) behind = true;
        q.push([((P.x + 1) / 2) * vw, ((1 - P.y) / 2) * vh]);
      }
      const xs = q.map((p) => p[0]), ys = q.map((p) => p[1]);
      const bx0 = Math.min(...xs), bx1 = Math.max(...xs), by0 = Math.min(...ys), by1 = Math.max(...ys);
      const inside = (Math.max(0, Math.min(vw, bx1) - Math.max(0, bx0)) * Math.max(0, Math.min(vh, by1) - Math.max(0, by0))) / Math.max(1, (bx1 - bx0) * (by1 - by0));
      const across = Math.hypot(q[1][0] - q[0][0], q[1][1] - q[0][1]);
      const cx = (bx0 + bx1) / 2, cy = (by0 + by1) / 2;
      const centred = !behind && cx > 0 && cx < vw && cy > 0 && cy < vh;
      // printed as large as reads well: wide when the board is at least about 500 px across on the
      // screen, compact from about 300; a little give either way so it doesn't flicker between them.
      // Its top must clear the top bar's label (walking, the view looks along the floor and the board
      // rises out of the top of it: then the plaque stands in). Held flat only from above, where a
      // phone shows the stage cut off at the side.
      const k = chrome();
      let m: Mode = 'off';
      if (!behind && inside > (mode === 'wide' || mode === 'compact' ? 0.86 : 0.92) && by0 > k.clear - 12) {
        if (across >= (mode === 'wide' ? 470 : 500)) m = 'wide';
        else if (across >= (mode === 'compact' ? 280 : 300)) m = 'compact';
      }
      // held flat: under the label, clear of the dock (beside it when there's room, as on a phone on
      // its side, else above it), as many dances as fit, two to four; none when two don't fit
      let fx = 0, fy = 0;
      if (m === 'off' && centred && !k.walking) {
        const w = Math.min(vw - 2 * k.g, 460), top = k.clear + 10;
        let x = Math.max(k.g, Math.min(vw - w - k.g, cx - w / 2)), bottom = vh - k.g;
        const d = k.dock;
        if (d && d.height && x < d.right + 8) { if (d.right + 8 + w <= vw - k.g) x = d.right + 8; else bottom = d.top - 10; }
        const rowH = w < 420 ? 70 : 56, fit = Math.min(4, Math.floor((bottom - top - 66) / rowH));
        if (fit >= 2) {
          m = 'flat';
          setMode(m);
          if (el.style.width !== `${w}px`) { el.style.width = `${w}px`; el.classList.toggle('narrow', w < 420); el.classList.toggle('stack', w < 420); flatH = -1; fitTitles = true; }
          if (fit !== flatPer) { flatPer = fit; showPage(); flatH = -1; }
          if (flatH < 0 || el.hidden) { el.hidden = false; flatH = el.offsetHeight; }
          fx = x; fy = Math.max(top, Math.min(Math.max(top, bottom - flatH), by0));
        }
      }
      setMode(m);
      if (el.hidden !== (m === 'off')) el.hidden = m === 'off';
      if (m === 'off') return;
      let t: string;
      if (m === 'flat') {
        t = `translate(${fx.toFixed(0)}px, ${fy.toFixed(0)}px)`;
      } else {
        const w = m === 'wide' ? WIDE_W : COMPACT_W;
        t = quadMatrix(w, Math.round(w * aspect), q);
      }
      if (t !== lastT) { el.style.transform = t; lastT = t; }
      if (fitTitles) {
        fitTitles = false;
        for (const li of rows) {
          if (li.hidden) continue;
          const tt = li.firstElementChild as HTMLElement;
          tt.classList.remove('long');
          if (tt.scrollWidth > tt.clientWidth + 1) tt.classList.add('long');
        }
      }
    },
    inView: () => on && mode !== 'off',
  };
}
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
